import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';

const LANGUAGES = [
  { code: 'et', flag: '🇪🇪', name: 'Eesti', label: 'ET' },
  { code: 'en', flag: '🇬🇧', name: 'English', label: 'ENG' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский', label: 'RUS' },
];

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
  };

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <div data-testid="language-switcher">
      {/* Desktop: flag buttons */}
      <div className="hidden md:flex items-center gap-1">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`text-2xl px-2 py-1 rounded-lg transition-all hover:bg-slate-100 ${
              i18n.language === lang.code
                ? 'bg-slate-100 ring-2 ring-sky-500'
                : 'opacity-60 hover:opacity-100'
            }`}
            title={lang.name}
            data-testid={`lang-${lang.code}`}
            aria-label={`Switch to ${lang.name}`}
          >
            {lang.flag}
          </button>
        ))}
      </div>

      {/* Mobile: custom dropdown menu (shadcn) */}
      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger
            className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500"
            data-testid="lang-trigger-mobile"
            aria-label="Select language"
          >
            <span>{current.label}</span>
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={6}
            className="min-w-[140px] bg-white border border-slate-200 shadow-lg rounded-lg p-1"
            data-testid="lang-menu-mobile"
          >
            {LANGUAGES.map((lang) => {
              const active = i18n.language === lang.code;
              return (
                <DropdownMenuItem
                  key={lang.code}
                  onSelect={() => changeLanguage(lang.code)}
                  className={`flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md cursor-pointer ${
                    active ? 'bg-sky-50 text-sky-700 font-semibold' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-lg leading-none">{lang.flag}</span>
                    <span>{lang.label}</span>
                  </span>
                  {active && <Check className="w-4 h-4" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
